/**
 * GroupDetail — the full-page GROUP record at /families/:id (P1, Felix
 * 2026-07-06). Production port of the APPROVED mockup:
 *   Deliverables/2026-07-06-group-record-fullpage-mockup.html
 *
 * Replaces the old in-list accordion (Families.tsx) with a first-class page,
 * built on the SAME shell as the Contact record (ClientDetail): a pinned 2-line
 * header + the L1A `StageTabBar` mechanism (here via GroupTabBar) + one panel
 * that swaps. The breadcrumb ("Groups › <name>", Groups → /families) is published
 * UP into the AppShell `.phead` chrome via `usePageBreadcrumb` — the same channel
 * the contact-record breadcrumb fix used.
 *
 * Tabs REUSE the existing group pieces (no rebuild):
 *   · Structure chart → the intake FamilyChart / OrgChart (via the shared
 *     members→persons converters exported from Families.tsx).
 *   · Members edit / Add member / Org billing → GroupRosterEditor /
 *     AddMemberInline / OrgBillingSection (exported from Families.tsx).
 *   · Astro-Vastu → the consultation-hub LAYOUT (member summary + Comparative
 *     Box Matrix). The astro engine (astro.aayatanveda.in) is NOT wired yet, so
 *     every astro value shows an HONEST pending state and the Download /
 *     Recalculate buttons are disabled-with-a-note — never a silent no-op.
 *
 * Owner-confirmed choices baked in: rounded-square icon tile; the member
 * summary KEEPS a "Chart" column; the org header Billing button jumps to the
 * Billing tab; DEFAULT TAB = Astro-Vastu for BOTH Family and Organisation.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { usePageBreadcrumb } from '../lib/pageChromeContext';
import { Btn3, EmptyNote } from '../components/ui';
import {
  GroupTabBar,
  FAMILY_TABS,
  ORG_TABS,
  type GroupTabKey,
} from '../components/GroupTabBar';
import { FamilyChart, OrgChart } from '../components/IntakeModule';
import {
  GroupRosterEditor,
  AddMemberInline,
  OrgBillingSection,
  GroupLocationSection,
  membersToFamilyPersons,
  membersToOrgPersons,
} from './Families';
import {
  deleteFamily,
  listenFamilies,
  type FamilyGroup,
  type FamilyMember,
} from '../lib/familyStore';
import { listenClients, type LiveClient } from '../lib/projectStore';
import { relationToClientLabel, workAxisLabel } from '../lib/p1Roles';
import {
  resolveAstroGroup,
  renderAstroPdf,
  type AstroGroupResolved,
} from '../lib/astroHome';

/** The 12 fixed Boxes (life contexts) — the Comparative Box Matrix rows. */
const BOXES: string[] = [
  'Personal Belongings & Accessories',
  'Valuables, Money, Family',
  'Perseverance and Presentation',
  'Building front and hiring',
  'Solution Providing, Hobby',
  'Serving Area',
  'Public Interaction, Assisting staff',
  'Maintenance, Behind Curtains',
  'Dharma, Meditation and higher Learnings',
  'Excel and Growth',
  'Friends and Party',
  'Foreign travelling, Daily Needs',
];

/** The five astro room-direction columns in the member summary (pending). */
const SUMMARY_COLS = ['Ascendant', 'Bedroom', 'Entrance', 'Office', 'Study'];

/** PURE — a member's display role label for the record (org vs family axis). */
function memberRole(group: FamilyGroup, m: FamilyMember): string {
  if (group.type === 'organization') {
    const raw = m.work_role || m.relationship || '';
    return raw ? workAxisLabel(raw) : '';
  }
  const raw = m.relation_to_client || m.relationship || '';
  return raw ? relationToClientLabel(raw) : '';
}

/** PURE — the org "reports to" name for a member's meta line, if resolvable. */
function reportsToName(group: FamilyGroup, m: FamilyMember): string {
  if (!m.reports_to) return '';
  const mgr = group.members.find((o) => o.contactId === m.reports_to);
  return mgr?.name ?? '';
}

/** PURE — pull a member's box row for box N (1..12); null when the engine has none. */
function boxCellFor(
  member: AstroGroupResolved['members'][number],
  boxNo: number,
): { sign: string; direction: string } | null {
  const row = member.boxMatrix.find((b) => b.box === boxNo);
  if (!row) return null;
  return { sign: row.sign || '—', direction: row.direction || '—' };
}

/**
 * GroupAstroTab — the consultation hub, WIRED to `resolveAstroGroup` /
 * `renderAstroPdf` (astroHome seam) with honest-until-deploy degradation.
 *
 * Three states, all crash-proof:
 *   · loading      — "Computing…" while the callable resolves.
 *   · resolved     — real member summary + Comparative Box Matrix + LIVE
 *                    Download / Recalculate / per-member View-chart controls.
 *   · unavailable  — the callable rejected (e.g. not-deployed → not-found) OR
 *                    returned zero members: the HONEST pending state (real member
 *                    names, `—` cells, DISABLED buttons with a visible note). No
 *                    dead control, no fabricated data, never a white-screen.
 *
 * A LIVE Download/View-chart whose PDF callable is unavailable degrades to an
 * honest toast — it never no-ops silently.
 */
function GroupAstroTab({ group, isOrg }: { group: FamilyGroup; isOrg: boolean }) {
  const { toast } = useToast();
  const members = group.members;
  const count = members.length;

  const [phase, setPhase] = useState<'loading' | 'resolved' | 'unavailable'>('loading');
  const [data, setData] = useState<AstroGroupResolved | null>(null);
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  // The personId whose individual chart is currently being fetched — guards the
  // per-member "View chart" button against re-entry / double-fire.
  const [viewBusyId, setViewBusyId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setPhase('loading');
    (async () => {
      const res = await resolveAstroGroup({ groupId: group.id });
      if (!alive) return;
      if (res && res.members.length > 0) {
        setData(res);
        setPhase('resolved');
      } else {
        // null (rejected / not-deployed) OR an empty roster from the engine —
        // both degrade to the honest pending state, never a crash.
        setData(null);
        setPhase('unavailable');
      }
    })();
    return () => {
      alive = false;
    };
  }, [group.id]);

  async function onRecalculate() {
    if (recalcBusy) return;
    setRecalcBusy(true);
    const res = await resolveAstroGroup({ groupId: group.id }, { recalc: true });
    if (res && res.members.length > 0) {
      setData(res);
      setPhase('resolved');
    } else {
      setPhase('unavailable');
      toast('Astro-Vastu recalculation is not available yet — it goes live once the engine is deployed.');
    }
    setRecalcBusy(false);
  }

  async function onDownloadPdf() {
    if (pdfBusy) return;
    setPdfBusy(true);
    // Surface 3 — the group's 23-page Astro-Vastu Report.
    const url = await renderAstroPdf({ groupId: group.id, kind: 'astro-vastu' });
    setPdfBusy(false);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast('The Astro-Vastu PDF is not available yet — it generates once the engine is deployed.');
    }
  }

  async function onViewMemberChart(personId: string) {
    if (viewBusyId) return; // ignore re-entry while a PDF request is in flight
    setViewBusyId(personId);
    try {
      // Surface 4 — THIS member's own 2-page Bhava Chakra birth chart.
      const url = await renderAstroPdf({ contactIds: [personId], kind: 'chart' });
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast('This individual chart is not available yet — it generates once the engine is deployed.');
      }
    } finally {
      setViewBusyId(null);
    }
  }

  const resolved = phase === 'resolved' && data !== null;
  const summaryRows = resolved ? data!.members : [];

  return (
    <div className="block">
      <h2>Astro-Vastu — consultation hub</h2>
      <p className="sub">
        Open this during a consultation: the {isOrg ? 'organisation' : 'family'}’s whole
        Astro-Vastu picture in one place — the full PDF, a per-
        {isOrg ? 'personnel' : 'member'} summary, and the Comparative Box Matrix.
      </p>
      <div className="astro-box">
        <div className="chart-title-row">
          <h3>{isOrg ? 'Organisation' : 'Family'} Astro-Vastu chart</h3>
          <span className="ch-kind astro">Astrology · directions</span>
          {phase === 'loading' && <span className="coming">⏳ computing…</span>}
          {phase === 'unavailable' && <span className="coming">⏳ stored on the group · coming</span>}
        </div>

        {phase === 'loading' ? (
          <p className="ch-desc" role="status" aria-live="polite">
            Computing the {isOrg ? 'organisation' : 'family'} Astro-Vastu chart from the
            engine — member summary and the Comparative Box Matrix…
          </p>
        ) : resolved ? (
          <p className="ch-desc">
            The engine-computed Astro-Vastu view — a per-{isOrg ? 'person' : 'member'} summary
            plus the Comparative Box Matrix. Download the full PDF, open an individual chart,
            or recalculate.
          </p>
        ) : (
          <p className="ch-desc">
            The real Astro-Vastu representation is computed from the astro engine — a per-
            {isOrg ? 'person' : 'member'} summary plus the Comparative Box Matrix. The engine
            is not wired to the group yet, so the values below are a pending state, never
            fabricated data.
          </p>
        )}

        {/* Actions — LIVE when resolved, honest-disabled otherwise (never a no-op) */}
        <div className="astro-actions">
          <button
            type="button"
            className="av-btn solid"
            onClick={resolved ? () => void onDownloadPdf() : undefined}
            disabled={!resolved || pdfBusy}
            aria-disabled={!resolved || pdfBusy}
            title={resolved ? 'Open the full Astro-Vastu PDF' : 'Available once the Astro-Vastu engine is deployed.'}
          >
            {pdfBusy ? '⏳ Preparing…' : '⬇ Download full Astro-Vastu PDF'}
          </button>
          <button
            type="button"
            className="av-btn"
            onClick={resolved ? () => void onRecalculate() : undefined}
            disabled={!resolved || recalcBusy}
            aria-disabled={!resolved || recalcBusy}
            title={resolved ? 'Recompute the charts from the engine' : 'Available once the Astro-Vastu engine is deployed.'}
          >
            {recalcBusy ? '↻ Recalculating…' : '↻ Recalculate charts'}
          </button>
        </div>
        {!resolved && (
          <p className="av-note">
            Both become live once the Astro-Vastu engine is deployed to the group.
          </p>
        )}

        {/* Member summary — wide (8 cols) → own horizontal rail so it never
            pushes a page-level scroll on mobile (mirrors the box matrix). */}
        <p className="av-sub">{isOrg ? 'Key-personnel summary' : 'Member summary'}</p>
        <div className="matrix-scroll">
        <table
          className="vgrid"
          aria-label={`${group.name} astro-vastu member summary${resolved ? '' : ' (pending)'}`}
        >
          <thead>
            <tr>
              <th>{isOrg ? 'Key person' : 'Member'}</th>
              <th>Role</th>
              {SUMMARY_COLS.map((c) => (
                <th key={c}>{c}</th>
              ))}
              <th>Chart</th>
            </tr>
          </thead>
          <tbody>
            {count === 0 ? (
              <tr>
                <td className="pending" colSpan={SUMMARY_COLS.length + 3}>
                  No {isOrg ? 'key personnel' : 'members'} yet — add people to build the summary.
                </td>
              </tr>
            ) : resolved ? (
              summaryRows.map((m) => (
                <tr key={m.personId}>
                  <td className="member">{m.name}</td>
                  <td>{m.relationToClient || '—'}</td>
                  <td>{m.ascendantSign || '—'}</td>
                  <td>{m.bedroomDir || '—'}</td>
                  <td>{m.entranceDir || '—'}</td>
                  <td>{m.officeDir || '—'}</td>
                  <td>{m.studyDir || '—'}</td>
                  <td className="chart-cell">
                    <button
                      type="button"
                      className="av-btn"
                      style={{ padding: '4px 10px', fontSize: 12 }}
                      onClick={() => void onViewMemberChart(m.personId)}
                      disabled={viewBusyId === m.personId}
                      aria-disabled={viewBusyId === m.personId}
                      title="Open this person’s individual astro chart"
                    >
                      {viewBusyId === m.personId ? '⏳ Preparing…' : '🔯 View chart'}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              members.map((m) => {
                const role = memberRole(group, m);
                return (
                  <tr key={m.contactId}>
                    <td className="member">{m.name}</td>
                    <td className="pending">{role || '—'}</td>
                    {SUMMARY_COLS.map((c) => (
                      <td className="pending" key={c}>—</td>
                    ))}
                    <td className="chart-cell">
                      <span className="chart-soon">🔯 coming</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>

        {/* Comparative Box Matrix — 12 fixed Boxes × members, own overflow-x */}
        <p className="av-sub">Comparative Box Matrix</p>
        <p className="ch-desc">
          Rows are the <strong>12 fixed Boxes</strong> (life contexts); each{' '}
          {isOrg ? 'key person' : 'member'} is a column split into the{' '}
          <strong>Rashi</strong> on that box and its Vastu <strong>Direction</strong>.
          {resolved ? '' : ' Values populate once the engine is deployed.'}
        </p>
        <div className="matrix-scroll">
          <table
            className="matrix"
            aria-label={`${group.name} comparative box matrix${resolved ? '' : ' (pending)'}`}
          >
            {!resolved && (
              <caption>
                Awaiting the engine — Rashi + Direction populate from the astro
                engine once the group chart is wired.
              </caption>
            )}
            <thead>
              <tr className="grp">
                <th className="boxlbl" rowSpan={2}>Box (life context)</th>
                {resolved
                  ? summaryRows.map((m) => (
                      <th key={m.personId} colSpan={2}>
                        {m.name}
                        {m.relationToClient ? ` · ${m.relationToClient}` : ''}
                      </th>
                    ))
                  : members.map((m) => {
                      const role = memberRole(group, m);
                      return (
                        <th key={m.contactId} colSpan={2}>
                          {m.name}
                          {role ? ` · ${role}` : ''}
                        </th>
                      );
                    })}
              </tr>
              <tr className="subrow">
                {resolved
                  ? summaryRows.map((m) => (
                      <Fragment key={m.personId}>
                        <th>Rashi</th>
                        <th>Dir</th>
                      </Fragment>
                    ))
                  : members.map((m) => (
                      <Fragment key={m.contactId}>
                        <th>Rashi</th>
                        <th>Dir</th>
                      </Fragment>
                    ))}
              </tr>
            </thead>
            <tbody>
              {BOXES.map((box, i) => (
                <tr key={box}>
                  <td className="boxlbl">
                    <b>{i + 1}</b> {box}
                  </td>
                  {resolved
                    ? summaryRows.map((m) => {
                        const cell = boxCellFor(m, i + 1);
                        return (
                          <Fragment key={m.personId}>
                            <td className="cell">{cell ? cell.sign : '—'}</td>
                            <td className="cell">{cell ? cell.direction : '—'}</td>
                          </Fragment>
                        );
                      })
                    : members.map((m) => (
                        <Fragment key={m.contactId}>
                          <td className="cell">—</td>
                          <td className="cell">—</td>
                        </Fragment>
                      ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="scroll-hint">
          <span aria-hidden="true">↔</span> The matrix scrolls sideways on its own — the page never does.
        </div>

        <div className="engine-note">
          <span className="ic" aria-hidden="true">🔯</span>
          <div>
            {resolved ? (
              <>
                Signs, directions and room-directions are{' '}
                <strong>engine-computed</strong>. Nothing here is fabricated — every cell
                reflects what the astro engine returned.
              </>
            ) : (
              <>
                Signs, directions and room-directions will be{' '}
                <strong>engine-computed</strong> once the group-level Astro-Vastu chart is
                deployed. Nothing here is fabricated — the cells stay a pending state until
                then.
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GroupDetail() {
  const { id = '' } = useParams();
  const gid = decodeURIComponent(id);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const actorName = user?.displayName || user?.email?.split('@')[0] || 'You';

  const [groups, setGroups] = useState<FamilyGroup[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [contacts, setContacts] = useState<LiveClient[]>([]);
  // DEFAULT TAB = Astro-Vastu (owner-locked) for BOTH Family and Organisation.
  const [activeTab, setActiveTab] = useState<GroupTabKey>('astro');
  const [editing, setEditing] = useState(false);
  const headRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const unsub = listenFamilies(
      (gs) => {
        setGroups(gs);
        setLoaded(true);
      },
      { onErr: () => { toast('Could not load groups (check connection)'); setLoaded(true); } },
    );
    return unsub;
  }, [toast]);

  // Contacts stream — only needed for the Add-member typeahead in Edit mode.
  useEffect(() => {
    const unsub = listenClients(setContacts, { onErr: () => { /* degrade quietly */ } });
    return unsub;
  }, []);

  const group = useMemo(() => groups.find((g) => g.id === gid) ?? null, [groups, gid]);
  const isOrg = group?.type === 'organization';
  const tabs = isOrg ? ORG_TABS : FAMILY_TABS;
  const count = group?.members.length ?? 0;

  // Pin the tab strip below the record header, which pins below the app chrome
  // (.phead) — the SAME mechanism as ClientDetail (--rec-phead-top/--rec-bar-top).
  useEffect(() => {
    const phead = document.querySelector('.phead') as HTMLElement | null;
    const apply = () => {
      const ph = phead?.offsetHeight ?? 0;
      const hd = headRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty('--rec-phead-top', `${ph}px`);
      document.documentElement.style.setProperty('--rec-bar-top', `${ph + hd}px`);
    };
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (phead) ro?.observe(phead);
    if (headRef.current) ro?.observe(headRef.current);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      document.documentElement.style.removeProperty('--rec-phead-top');
      document.documentElement.style.removeProperty('--rec-bar-top');
    };
  }, []);

  // SINGLE breadcrumb — published UP into the AppShell top chrome (Groups →
  // /families, then this group's name). Called BEFORE any early return so the
  // hook is unconditional (rules of hooks).
  usePageBreadcrumb([
    { label: 'Groups', href: '/families' },
    { label: group ? group.name : loaded ? gid : 'Loading…' },
  ]);

  // Keep the panel valid if a family somehow holds the org-only Billing tab.
  useEffect(() => {
    if (activeTab === 'billing' && group && !isOrg) setActiveTab('details');
  }, [activeTab, group, isOrg]);

  async function handleDelete() {
    if (!group) return;
    if (!confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;
    try {
      await deleteFamily(group.id, group.members);
      toast(`Deleted "${group.name}".`);
      navigate('/families');
    } catch (e) {
      toast(`Delete failed: ${(e as Error).message}`);
    }
  }

  // ── Loading / not-found (honest) ──
  if (!loaded && !group) {
    return (
      <div className="group-record">
        <p className="rec-loading">Loading group…</p>
      </div>
    );
  }
  if (loaded && !group) {
    return (
      <div className="group-record">
        <EmptyNote title="Group not found">
          No group exists for &ldquo;{gid}&rdquo;.{' '}
          <Link to="/families">Back to Groups</Link>.
        </EmptyNote>
      </div>
    );
  }
  if (!group) return null; // (unreachable — narrows the type below)

  const members = group.members;
  const memberWord = isOrg
    ? count === 1 ? 'key person' : 'key personnel'
    : count === 1 ? 'member' : 'members';
  const hasBilling = Boolean(
    (group.gstNumber ?? group.pan ?? group.orgEmail ?? group.orgPhone ?? group.billingAddress ?? '').trim(),
  );
  const persons = isOrg ? membersToOrgPersons(members) : membersToFamilyPersons(members);
  const hasStructure = isOrg
    ? persons.some((p) => p.work_role || p.reports_to)
    : persons.some((p) => p.relation_to_client);
  const rosterKey = `${group.id}:${members.map((m) => m.contactId).join(',')}`;
  // Resolve the billing-contact-person FK (a `C#####` doc id) against the loaded
  // contacts stream so the Billing read view can show the person's name + link.
  const billingContact = group.billingContactId
    ? contacts.find((c) => c.cid === group.billingContactId) ?? null
    : null;

  return (
    <div className="group-record">
      {/* Back-navigation is the breadcrumb "Groups › <name>" (Groups → /families)
          published into the AppShell top chrome — no separate Back control. */}

      {/* ── PINNED HEADER (2 lines) ── */}
      <section className="rec-head-card" ref={headRef}>
        <div className="rec-head">
          <div className={`avatar group-tile ${isOrg ? 'org' : 'fam'}`} aria-hidden="true">
            {isOrg ? '🏢' : '👪'}
          </div>
          <div className="rh-nameblock">
            <div className="rh-name">{group.name}</div>
            <span className={`type-pill ${isOrg ? 'org' : 'family'}`}>
              <span className="dot">●</span> {isOrg ? 'Organisation' : 'Family'}
            </span>
          </div>
          <div className="rh-actions">
            <Btn3
              intent={editing ? 'grn' : 'ink'}
              size="sm"
              onClick={() => {
                setEditing((e) => {
                  const next = !e;
                  if (next) setActiveTab('members');
                  return next;
                });
              }}
            >
              {editing ? 'Done editing' : 'Edit'}
            </Btn3>
            {isOrg && (
              <Btn3 intent="ghost" size="sm" onClick={() => setActiveTab('billing')}>
                Billing
              </Btn3>
            )}
          </div>
        </div>
        <div className="rh-meta">
          {/* Friendly G-### number when minted; falls back to the raw doc id for
              legacy / number-less groups until the backfill sweep numbers them. */}
          Group <span className="msep">·</span> <strong>#{group.groupNo ?? group.id}</strong>
          <span className="msep">·</span> <strong>{count} {memberWord}</strong>
          {isOrg && hasBilling && (
            <>
              <span className="msep">·</span> bills to the company
            </>
          )}
          {group.astroFamilyId && (
            <>
              <span className="msep">·</span> Astro-linked
            </>
          )}
        </div>
      </section>

      {/* ── TAB STRIP (default = Astro-Vastu) ── */}
      <GroupTabBar items={tabs} activeKey={activeTab} onSelect={setActiveTab} />

      {/* ── ONE panel that swaps ── */}
      <div
        className="panel-wrap"
        id="group-panel"
        role="tabpanel"
        aria-labelledby={`gtab-${activeTab}`}
        tabIndex={0}
      >
        {/* ---- DETAILS ---- */}
        {activeTab === 'details' && (
          <div className="block">
            <h2>Group details</h2>
            <div className="fields">
              <div className="f">
                <label>{isOrg ? 'Organisation name' : 'Group name'}</label>
                <div className="val">{group.name}</div>
              </div>
              <div className="f">
                <label>Type</label>
                <div className="val">{isOrg ? 'Organisation' : 'Family'}</div>
              </div>
              {group.description && (
                <div className="f span2">
                  <label>Description</label>
                  <div className="val wide">{group.description}</div>
                </div>
              )}
              {/* Primary location — read value here; edited via GroupLocationSection
                  below when in Edit (free text, families AND organisations). */}
              {!editing && (
                <div className="f">
                  <label>Primary location</label>
                  <div className="val">{group.location || '—'}</div>
                </div>
              )}
              <div className="f">
                <label>{isOrg ? 'Key personnel' : 'Members'}</label>
                <div className="val">{count}</div>
              </div>
              {group.astroFamilyId && (
                <div className="f">
                  <label>Astro link</label>
                  <div className="val">
                    Linked · {group.astroMemberCount ?? '—'} synced
                  </div>
                </div>
              )}
            </div>
            {editing && (
              <GroupLocationSection
                group={group}
                actorName={actorName}
                onDone={(msg) => toast(msg)}
              />
            )}
          </div>
        )}

        {/* ---- MEMBERS / KEY PERSONNEL ---- */}
        {activeTab === 'members' && (
          <div className="block">
            <h2>
              {isOrg ? 'Key personnel' : 'Members'} <span className="count">· {count}</span>
            </h2>
            <p className="sub">
              {isOrg
                ? 'Each key person is a Contact, with their work role / reporting line and their own individual astro chart. Use Edit to change roles or add people.'
                : 'Each member is a Contact, with their relationship to the family and their own individual astro chart. Use Edit to change relationships or add people.'}
            </p>

            {editing ? (
              <>
                {count === 0 ? (
                  <p className="note-inline">No members yet — add someone below.</p>
                ) : (
                  <GroupRosterEditor
                    key={rosterKey}
                    group={group}
                    actorName={actorName}
                    onDone={(msg) => toast(msg)}
                  />
                )}
                <AddMemberInline
                  group={group}
                  contacts={contacts}
                  actorName={actorName}
                  onDone={(msg) => toast(msg)}
                />
                <hr className="hr" />
                {/* Recalculate — honest pending (no astro recalc callable yet) */}
                <div className="astro-actions">
                  <button
                    type="button"
                    className="av-btn"
                    disabled
                    aria-disabled="true"
                    title="Available once the Astro-Vastu engine is wired."
                  >
                    ↻ Recalculate astro charts
                  </button>
                  <span className="av-note">
                    Recalculation becomes available once the Astro-Vastu engine
                    (astro.aayatanveda.in) is wired to the group.
                  </span>
                </div>
                <div style={{ marginTop: 'var(--s-5)' }}>
                  <Btn3 intent="ghost" size="sm" onClick={() => void handleDelete()}>
                    Delete group
                  </Btn3>
                </div>
              </>
            ) : count === 0 ? (
              <EmptyNote title="No members yet">
                Use <b>Edit</b> (top-right) to add {isOrg ? 'key personnel' : 'members'} to this group.
              </EmptyNote>
            ) : (
              <div className="rows">
                {members.map((m) => {
                  const role = memberRole(group, m);
                  const mgr = reportsToName(group, m);
                  return (
                    <div className="mrow" key={m.contactId}>
                      <Link className="m-name" to={`/contacts/${encodeURIComponent(m.contactId)}`}>
                        {m.name}
                      </Link>
                      {role && <span className="m-rel">{role}</span>}
                      <span className="push" />
                      {isOrg && mgr && <span className="m-meta">Reports to: {mgr}</span>}
                      {/* Honest per-member chart affordance — muted, non-interactive */}
                      <span className="chart-soon" title="Individual astro chart — available once the astro engine is wired.">
                        🔯 Chart · coming
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ---- STRUCTURE CHART (blue) ---- */}
        {activeTab === 'structure' && (
          <div className="block">
            <h2>{isOrg ? 'Organisation structure chart' : 'Family structure chart'}</h2>
            <p className="sub">
              {isOrg
                ? 'The reporting hierarchy — built from each person’s work role / reports-to. Who reports to whom, not astrology.'
                : 'Who is related to whom — the kinship tree, built from each member’s relationship. This is not astrology (that lives on the Astro-Vastu tab).'}
            </p>
            <div className="struct-box">
              <div className="chart-title-row">
                <h3>{isOrg ? 'Reporting tree' : 'Kinship tree'}</h3>
                <span className="ch-kind">
                  {isOrg ? 'Structure · reporting' : 'Structure · kinship'}
                </span>
              </div>
              <p className="ch-desc">
                {isOrg
                  ? 'The org chart — not astrology.'
                  : 'The relationship diagram — the family tree, not astrology.'}
              </p>
              {hasStructure ? (
                <div className="chart-canvas">
                  {isOrg ? <OrgChart persons={persons} /> : <FamilyChart persons={persons} />}
                </div>
              ) : (
                <p className="note-inline">
                  {isOrg
                    ? 'Set each person’s work role and reports-to (via Edit → Key personnel) to see the reporting hierarchy.'
                    : 'Set each member’s relation (via Edit → Members) to see the family tree.'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ---- ASTRO-VASTU — the consultation hub (wired · honest-until-deploy) ---- */}
        {activeTab === 'astro' && (
          <GroupAstroTab group={group} isOrg={isOrg} />
        )}

        {/* ---- BILLING (Organisation only) ---- */}
        {activeTab === 'billing' && isOrg && (
          <div className="block">
            <h2>Billing details</h2>
            <p className="sub">
              Needed to bill the organisation — the invoice goes to the company,
              not a person. Asked once, reused for every invoice. Use Edit to change.
            </p>
            {editing ? (
              <OrgBillingSection
                group={group}
                contacts={contacts}
                actorName={actorName}
                onDone={(msg) => toast(msg)}
              />
            ) : (
              <div className="fields">
                <div className="f">
                  <label>GST number</label>
                  <div className="val">{group.gstNumber || '—'}</div>
                </div>
                <div className="f">
                  <label>PAN</label>
                  <div className="val">{group.pan || '—'}</div>
                </div>
                <div className="f">
                  <label>Billing email</label>
                  <div className="val">{group.orgEmail || '—'}</div>
                </div>
                <div className="f">
                  <label>Billing phone</label>
                  <div className="val">{group.orgPhone || '—'}</div>
                </div>
                <div className="f span2">
                  <label>Billing address</label>
                  <div className="val wide">{group.billingAddress || '—'}</div>
                </div>
                {/* Billing contact person — resolve the C##### FK to the Contact's
                    name and link through; honest fallbacks when unset / unresolved. */}
                <div className="f">
                  <label>Billing contact person</label>
                  <div className="val">
                    {billingContact ? (
                      <Link to={`/contacts/${encodeURIComponent(billingContact.cid)}`}>
                        {billingContact.name}
                      </Link>
                    ) : group.billingContactId ? (
                      group.billingContactId
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
